'use client';

/**
 * DemoFrontierCard — the latest scene's search results, as test_run_view painted them.
 *
 * test_run_view normalises whichever result shape a scene's capability returned (product_search's flat
 * `products[]`, the SearchResults `products.results[]`) into one `results` slice: title, summary and up to 8 tiles,
 * every string painted server-side. This card renders that slice and nothing else. Self-suppresses when no scene
 * returned results (ingest-shape cases).
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useQuery } from '@tanstack/react-query';
import { Grid3X3, ImageOff } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useDemoAskStore } from '@/lib/stores/demo-ask-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { ResultTile, TestRunView } from './demo-test-run-types';


export function DemoFrontierCard() {
  const T = useCardTypography();
  const simulationId = useDemoAskStore((s) => s.testRun?.simulation_id ?? null);

  const { data } = useQuery<TestRunView>({
    queryKey: ['test_run_view', simulationId],
    queryFn: async () => {
      const res = await api.getStrategyData<TestRunView>('test_run_view', {
        simulation_id: simulationId,
      });
      if (!res.success) throw new Error(res.error || 'test_run_view failed');
      return res.data;
    },
    enabled: Boolean(simulationId),
    staleTime: 30_000,
  });

  if (!simulationId || !data) return null;

  const results = [...data.scenes].reverse().find((s) => s.results && s.results.tiles.length > 0)?.results;
  if (!results) return null;

  return (
    <div className="bg-card/30 border border-border rounded-2xl px-5 py-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Grid3X3 className="w-4 h-4 text-muted-foreground" />
          <div className={cn(T.body, 'text-sm font-medium')}>{results.title}</div>
        </div>
        <div className={cn(T.metadata, 'text-muted-foreground text-xs')}>{results.summary}</div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {results.tiles.map((tile) => (
          <ProductTile key={tile.sid} tile={tile} />
        ))}
      </div>
    </div>
  );
}


function ProductTile({ tile }: { tile: ResultTile }) {
  const T = useCardTypography();

  return (
    <div className="bg-muted/30 rounded-lg p-2 text-center group">
      <div className="aspect-square bg-muted/50 rounded mb-1.5 flex items-center justify-center overflow-hidden">
        {tile.image_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={tile.image_url} alt={tile.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <ImageOff className="w-4 h-4 text-muted-foreground/40" />
        )}
      </div>
      <div className={cn(T.body, 'text-[11px] font-medium leading-tight line-clamp-2')} title={tile.name}>
        {tile.name}
      </div>
      {tile.caption && (
        <div className={cn(T.metadata, 'text-[10px] text-muted-foreground mt-0.5 truncate')}>{tile.caption}</div>
      )}
    </div>
  );
}
